import { CardEvent } from "agronomist-ui-components";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { SwiperSlide } from "swiper/react";
import { useDispatch } from "react-redux";
import Section from "./Section";
import {
  getEventsRecent,
  getEventsRecentNext,
  getEventsRecentPrev,
} from "../../../services/redux/events/eventsAll/reducer";
import { useLinks } from "../../../services/helpers/links";
import { CarouselWrapper } from "./CarouselWrapper";

export const EventsSectionCarousel = ({ background, className = {} }) => {
  const { t } = useTranslation(["cft", "globals"]);
  const dispatch = useDispatch();
  const { event: eventLink } = useLinks();
  const [events, setEvents] = useState({
    results: [],
    next: null,
    previous: null,
    count: 0,
    allPrev: 1,
    allNext: 0,
  });
  const [isNextLoaded, setIsNextLoaded] = useState(false);
  const [isPrevLoaded, setIsPrevLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const swiper = useRef(null);

  const getPrev = url => {
    if (url) {
      setIsLoading(true);
      dispatch(getEventsRecentPrev(url))
        .then(({ payload: { status, data } }) => {
          if (status === 200) {
            const arr = events.results;
            arr.unshift(
              ...data.results.reverse().map((result, idx) => ({
                ...result,
                count: -(events.allPrev + idx),
              }))
            );

            setEvents(oldEvents => ({
              ...oldEvents,
              results: arr.sort((a, b) => a.count - b.count),
              previous: data.previous,
              allPrev: oldEvents.allPrev + data.results.length,
            }));
            swiper.current.swiper.slideTo(data.results.length, 0);
            setTimeout(() => swiper.current.swiper.slidePrev(), 100);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const getNext = url => {
    if (url) {
      setIsLoading(true);
      dispatch(getEventsRecentNext(url))
        .then(({ payload: { status, data } }) => {
          if (status === 200) {
            const arr = events.results;
            arr.unshift(
              ...data.results.map((result, idx) => ({
                ...result,
                count: events.count + idx,
              }))
            );

            setEvents(oldEvents => ({
              ...oldEvents,
              results: arr.sort((a, b) => a.count - b.count),
              next: data.next,
              allNext: oldEvents.allNext + data.results.length,
              count: oldEvents.count + data.results.length,
            }));
            swiper.current.swiper.update();
            swiper.current.swiper.slideNext();
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    try {
      if (!events.results.length) {
        dispatch(getEventsRecent()).then(({ payload: { status, data } }) => {
          if (status === 200) {
            setEvents(oldEvents => ({
              ...oldEvents,
              ...data,
              results: data.results.map((result, idx) => ({
                ...result,
                count: idx,
              })),
              count: data.results.length,
            }));
            if (data.next) {
              dispatch(getEventsRecentNext(data.next)).then(
                ({ payload: { status: nextStatus, data: nextData } }) => {
                  if (nextStatus === 200) {
                    setEvents(oldEvents => ({
                      ...oldEvents,
                      results: [
                        ...oldEvents.results,
                        ...nextData.results.map((result, idx) => ({
                          ...result,
                          count: idx + oldEvents.count,
                        })),
                      ],
                      next: nextData.next,
                      allNext: oldEvents.allNext + data.results.length,
                      count: oldEvents.count + nextData.results.length,
                    }));
                    setIsNextLoaded(true);
                  }
                }
              );
            } else setIsNextLoaded(true);

            if (data.previous) {
              dispatch(getEventsRecentPrev(data.previous)).then(
                ({ payload: { status: prevStatus, data: prevData } }) => {
                  if (prevStatus === 200) {
                    setEvents(oldEvents => ({
                      ...oldEvents,
                      results: [
                        ...prevData.results
                          .reverse()
                          .map((result, idx) => ({
                            ...result,
                            count: -(idx + oldEvents.allPrev),
                          }))
                          .sort((a, b) => a.count - b.count),
                        ...oldEvents.results,
                      ],
                      previous: prevData.previous,
                      allPrev: oldEvents.allPrev + prevData.results.length,
                    }));
                    setIsPrevLoaded(true);
                  }
                }
              );
            } else setIsPrevLoaded(true);
          }
        });
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (isNextLoaded && isPrevLoaded && !!events.results.length) {
      swiper.current.swiper.slideTo(events.results.length - events.allNext, 0);
    }
  }, [isNextLoaded, isPrevLoaded, events]);

  return (
    <div className={`Sections--${background}`}>
      <Container>
        <Section
          className={classNames({
            className,
          })}
          title={<h2>{t("globals:events.title")}</h2>}
        >
          <>
            <div className="Sections__categories">
              <Link
                to={t("urlsNames:events.all")}
                className="Sections__more Sections__more--higher"
              >
                {t("globals:events.buttonAll")}
              </Link>
            </div>

            <CarouselWrapper
              swiperRef={swiper}
              showNav={!!events.results.length}
              onPrev={() => getPrev(events.previous)}
              onNext={() => getNext(events.next)}
              length={events.results.length - 1}
              showPag={false}
              isLoading={isLoading}
            >
              {events.results ? (
                events.results.map(el => {
                  return (
                    <SwiperSlide
                      virtualIndex={el.count}
                      key={el.count}
                      style={{ height: "auto" }}
                    >
                      <CardEvent
                        url={eventLink(el.slug)}
                        key={el.id}
                        id={el.id}
                        title={el.title}
                        location={el.location_title}
                        dateFrom={el.started_at}
                        dateTo={el.ended_at}
                      />
                    </SwiperSlide>
                  );
                })
              ) : (
                <h2 style={{ margin: "0 auto" }}>
                  {t("globals:events.noEvents")}
                </h2>
              )}
            </CarouselWrapper>
          </>
        </Section>
      </Container>
    </div>
  );
};
